/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../../ts';
import { Assistant } from '../../../../ts/assistant';
import { AsyncAppThunk, CreateAsyncThunk } from '../../../../ts/state';
import * as API from '../../api/assistants';
import { getErrorString } from '../../util/errorHandler';
import {
  createdObjectEvents,
  deletedObjectEvents,
  updatedObjectEvents
} from '../../util/trackers/segment';
import { isCacheExpired } from '../helper';
import { selectIsNewIa } from '../instance/selectors';
import { addErrorMessage } from '../messages/actions';
import { selectAllAssistants } from './core-selectors';

const TIMEOUT = 60000; // 60s in millis

// Async actions
export const listAssistants = createAsyncThunk<
  Assistant[],
  void,
  CreateAsyncThunk
>(
  'assistants/list',
  async (_, { dispatch, getState }) => {
    try {
      const new_ia = selectIsNewIa(getState());
      const response = await API.listAssistants({ new_ia });
      return response.assistants;
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (_, { getState }) => {
      const { assistants } = getState();
      return assistants.listStatus !== ApiStatus.LOADING;
    }
  }
);

export const getAssistant = createAsyncThunk<
  Assistant,
  { assistantId: string },
  CreateAsyncThunk
>(
  'assistants/get',
  async (params, { dispatch }) => {
    try {
      const { assistantId } = params;
      return await API.getAssistant(assistantId);
    } catch (error) {
      dispatch(addErrorMessage(error));
      throw error;
    }
  },
  {
    condition: (assistantId, { getState }) => {
      const { assistants } = getState();
      // use the pendingGetStatus to track per assistantId, return FALSE to block
      const fetchStartTime =
        assistants?.pendingGetStatus?.[assistantId.assistantId];

      return !fetchStartTime || Date.now() - fetchStartTime > TIMEOUT;
    }
  }
);

export const listAssistantsIfRequired = (): AsyncAppThunk<
  Promise<Assistant[]>
> => {
  return async (dispatch, getState) => {
    const { listFetched } = getState()?.assistants;
    // Avoid requests if data is not stale
    if (!isCacheExpired(listFetched)) {
      return Promise.resolve(selectAllAssistants(getState()));
    }
    await dispatch(listAssistants());
    return selectAllAssistants(getState());
  };
};

export const createAssistant = createAsyncThunk<
  Assistant,
  Partial<Assistant>,
  CreateAsyncThunk
>('assistants/create', async (params, { dispatch, getState }) => {
  let new_ia = false;
  try {
    new_ia = selectIsNewIa(getState());
    const response = await API.createAssistant(params, { new_ia });
    // New IA tracks creation of assistants in `/onboarding/actions`
    if (!new_ia) {
      createdObjectEvents.assistant({
        object: response.assistant_id,
        parentPageName: 'create assistant wizard classic',
        resultValue: 'success',
        successFlag: true
      });
    }
    // need to dispatch a listAssistants because Roles are not returned on the create-POST
    dispatch(listAssistants());
    return response;
  } catch (error) {
    dispatch(addErrorMessage(error));
    // Track error in Analytics
    if (!new_ia) {
      createdObjectEvents.assistant({
        object: null, //there is no object in the failed case.
        parentPageName: 'create assistant wizard classic',
        resultValue: getErrorString(error),
        successFlag: false
      });
    }
    throw error;
  }
});

export const updateAssistant = createAsyncThunk<
  Assistant,
  Partial<Assistant> & { append?: boolean },
  CreateAsyncThunk
>('assistants/update', async (params, { dispatch }) => {
  const { assistant_id: id, append, ...rest } = params;

  try {
    const response = await API.updateAssistant(
      id,
      rest,
      append ? { append } : {}
    );
    updatedObjectEvents.assistant(
      {
        object: id,
        resultValue: 'success',
        successFlag: true
      },
      {
        waEnvironmentId: id
      }
    );
    return response;
  } catch (error) {
    dispatch(addErrorMessage(error));
    updatedObjectEvents.assistant(
      {
        object: id,
        resultValue: getErrorString(error),
        successFlag: false
      },
      {
        waEnvironmentId: id
      }
    );
    throw error;
  }
});

export const deleteAssistant = createAsyncThunk<
  string,
  string,
  CreateAsyncThunk
>('assistants/delete', async (id, { dispatch, getState }) => {
  let new_ia = false;
  try {
    new_ia = selectIsNewIa(getState());
    await API.deleteAssistant(id);

    // New IA tracks deletion of assistants in `/onboarding/actions`
    if (!new_ia) {
      deletedObjectEvents.assistant({
        object: id,
        resultValue: 'success',
        successFlag: true
      });
    }
    return id;
  } catch (error) {
    dispatch(addErrorMessage(error));
    if (!new_ia) {
      deletedObjectEvents.assistant({
        object: id,
        resultValue: getErrorString(error),
        successFlag: false
      });
    }
    throw error;
  }
});
